<template>
  <d-container
    fluid
    :class="{
      'main-content-container': true,
      'px-4': !isOrderView,
      'px-0': isOrderView
    }">
    <!-- Page Header -->
    <d-row
      no-gutters
      :class="{
        'page-header': true,
        'py-4': !isOrderView,
        'mb-4 pb-3 border-bottom': isOrderView
      }">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span
          v-if="!isOrderView"
          class="text-uppercase page-subtitle">
          {{ $t('complaints.complaints') }}
        </span>
        <h3 class="page-title">
          {{ $t('complaints.complaintsList') }}
        </h3>
      </d-col>
      <d-col
        v-if="isOrderView"
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="addNewComplaint">
          <i class="material-icons">add</i>
          {{ $t('complaints.addNewComplaint') }}
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="!isOrderView"
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="complaints-filters-form"
        tag="form"
        style="width: 100%;">
        <div class="d-flex">
          <d-input
            :placeholder="$t('complaints.enterOrderName')"
            v-model="filters.order_name" />

          <v-select
            class="ml-2 w-50"
            :options="complaintsWorkerOptions"
            v-model="filters.created_by_id"
            :placeholder="$t('complaints.createdBy')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            class="ml-2 w-50"
            :options="complaintsWorkerOptions"
            v-model="filters.handled_by_id"
            :placeholder="$t('complaints.handledBy')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            class="ml-2 w-50"
            :options="complaintsStatusOptions"
            v-model="filters.status"
            :placeholder="$t('status.statusQuestionMark')"
            :searchable="false"
            label="text">
          </v-select>

          <d-button
            theme="info"
            outline
            class="text-nowrap ml-2"
            @click.prevent="validateFiltersForm">
            {{ $t('ui.search') }}
          </d-button>

          <d-button
            theme="danger"
            outline
            class="text-nowrap ml-2"
            @click.prevent="clearFilters">
            {{ $t('ui.resetFilters') }}
          </d-button>
        </div>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" style="width: 150px;">{{ $t('complaints.status') }}</th>
            <th
              v-if="!isOrderView"
              scope="col" class="border-0">
              {{ $t('ui.order') }}
            </th>
            <th scope="col" class="border-0" style="width: 200px;">{{ $t('complaints.createdAt') }}</th>
            <th scope="col" class="border-0" style="width: 200px;">{{ $t('complaints.complainedElements') }}</th>
            <th scope="col" class="border-0" style="width: 200px;">{{ $t('complaints.statuses') }}</th>
            <th scope="col" class="border-0" style="width: 200px;">{{ $t('complaints.createdBy') }}</th>
            <th scope="col" class="border-0" style="width: 200px">{{ $t('complaints.handledBy') }}</th>
            <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(complaint, index) of complaints"
            :key="'complaint-' + index">
            <td class="align-middle">
              <d-badge
                v-if="complaint.status === 'IN_REALIZATION'"
                theme="danger"
                :outline="true">
                {{ $t('complaints.inRealization') }}
              </d-badge>
              <d-badge
                v-else-if="complaint.status === 'ENDED'"
                theme="success"
                :outline="true">
                {{ $t('complaints.ended') }}
              </d-badge>
            </td>
            <td
              v-if="!isOrderView"
              class="align-middle">
              <span
                class="text-primary cursor-pointer"
                @click="$router.push('/zamowienia/edycja/' + complaint.order_id)">
                {{ complaint.order_name }}
              </span>
            </td>
            <td class="align-middle">
              {{ complaint.created_at }}
            </td>
            <td class="align-middle">
              <small>
                {{ $t('complaints.complainedProductsCount') }}:
                <strong :class="complaint.products_count ? 'text-danger' : 'text-gray'">
                  {{ complaint.products_count }}
                </strong>
              </small>
              <small>
                {{ $t('complaints.complainedServicesCount') }}:
                <strong :class="complaint.services_count ? 'text-danger' : 'text-gray'">
                  {{ complaint.services_count }}
                </strong>
              </small>
              <small>
                {{ $t('complaints.complainedShipmentsCount') }}:
                <strong :class="complaint.shipments_count ? 'text-danger' : 'text-gray'">
                  {{ complaint.shipments_count }}
                </strong>
              </small>
            </td>
            <td class="align-middle">
              <small>
                <strong :class="complaint.items_status_RECEIVED_count ? 'text-primary' : 'text-gray'">
                  {{ complaint.items_status_RECEIVED_count }}
                </strong>
                {{ $t('complaints.itemsStatusReceived') }}
              </small><br>
              <small>
                <strong :class="complaint.items_status_NEGOTIATED_count ? 'text-primary' : 'text-gray'">
                  {{ complaint.items_status_NEGOTIATED_count }}
                </strong>
                {{ $t('complaints.itemsStatusNegotiated') }}
              </small><br>
              <small>
                <strong :class="complaint.items_status_ACCEPTED_count ? 'text-success' : 'text-gray'">
                  {{ complaint.items_status_ACCEPTED_count }}
                </strong>
                {{ $t('complaints.itemsStatusAccepted') }}
              </small><br>
              <small>
                <strong :class="complaint.items_status_REJECTED_count ? 'text-danger' : 'text-gray'">
                  {{ complaint.items_status_REJECTED_count }}
                </strong>
                {{ $t('complaints.itemsStatusRejected') }}
              </small>
            </td>
            <td class="align-middle">
              {{ complaint.created_by_name }}
            </td>
            <td class="align-middle">
              {{ complaint.handled_by_name }}
            </td>
            <td class="align-middle">
              <d-button
                theme="primary"
                @click.prevent="editCompliant(complaint.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.prevent="removeComplaint(complaint.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="complaints.length === 0">
            <td
              :colspan="isOrderView ? 7 : 8"
              class="text-center">
              {{ $t('complaints.noComplaintsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <complaint-edit-popup
      v-if="userIsAdminOrWorker && showComplaintEditPopup"
      :complaintID="parseInt(this.selectedComplaintID, 10)" />

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import ComplaintEditPopup from './../components/complaints/ComplaintEditPopup.vue';
import debounce from 'lodash.debounce';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'complaints',
  mixins: [
    HasFilters,
    HasPagination
  ],
  props: {
    isOrderView: {
      required: false,
      default: false
    },
    orderID: {
      required: false,
      default: false
    }
  },
  components: {
    'complaint-edit-popup': ComplaintEditPopup,
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedWorkers;
    },
    complaintsWorkerOptions () {
      return this.workers.map(worker => ({
        text: worker.name,
        value: worker.id
      }));
    },
    complaintsStatusOptions () {
      return [
        {
          text: this.$t('complaints.inRealization'),
          value: 'IN_REALIZATION'
        },
        {
          text: this.$t('complaints.ended'),
          value: 'ENDED'
        }
      ];
    },
    disableFiltersInSubview () {
      return this.isOrderView;
    },
    userIsAdminOrWorker () {
      return this.$store.getters.getUserType === 'admin' || this.$store.getters.getUserType === 'worker';
    }
  },
  data() {
    return {
      complaints: [],
      workers: [],
      loaded: false,
      loadError: false,
      loadedWorkers: false,
      removeInProgress: false,
      skipLSPagination: false,
      selectedComplaintID: null,
      showComplaintEditPopup: false,
      filters: {
        order_name: '',
        created_by_id: null,
        handled_by_id: null,
        status: null
      }
    };
  },
  created () {
    if (this.isOrderView) {
      this.skipLSPagination = true;
    }
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
    this.$bus.$on('complaints-refresh-list', this.loadFilteredData);
    this.$bus.$on('show-complaint-edit-popup', this.openComplaintEditPopup);
    this.$bus.$on('close-complaint-edit-popup', this.closeComplaintEditPopup);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {};

      if (this.isOrderView && this.orderID) {
        where.order_id = this.orderID;
      }

      if (this.filters.order_name) {
        where.search = this.filters.order_name;
      }

      if (this.filters.created_by_id && this.filters.created_by_id.value) {
        where.created_by_id = this.filters.created_by_id.value;
      }

      if (this.filters.handled_by_id && this.filters.handled_by_id.value) {
        where.handled_by_id = this.filters.handled_by_id.value;
      }

      if (this.filters.status !== null) {
        where.status = this.filters.status.value;
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/complaints/items?page=' + this.currentPage,
        listField: 'complaints',
        params: {
          where
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        loadedKey: 'loadedWorkers',
        params: {
          return: 'get',
          where: {
            type: ['worker', 'admin']
          }
        },
        outputKey: 'workers',
        errorKey: 'loadError'
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        order_name: '',
        created_by_id: null,
        handled_by_id: null,
        status: null
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['complaints-filters-form'], this.debouncedLoadFilteredData);
    },
    removeComplaint (complaintID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/complaints/delete/',
        id: complaintID,
        successTitle: this.$t('complaints.removeComplaintSuccessTitle'),
        successText: this.$t('complaints.removeComplaintSuccessText'),
        confirmTitle: this.$t('complaints.removeComplaintConfirmTitle'),
        confirmText: this.$t('complaints.deleteComplaintText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('complaints.removeComplaintErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    addNewComplaint () {
      this.$bus.$emit('show-complaint-add-popup');
    },
    editCompliant (compliantID) {
      this.$bus.$emit('show-complaint-edit-popup', compliantID);
    },
    openComplaintEditPopup (complaintID) {
      this.selectedComplaintID = complaintID;
      this.showComplaintEditPopup = true;
    },
    closeComplaintEditPopup () {
      this.selectedComplaintID = null;
      this.showComplaintEditPopup = false;
    }
  },
  beforeDestroy () {
    this.$bus.$off('complaints-refresh-list', this.loadFilteredData);
    this.$bus.$off('show-complaint-edit-popup', this.openComplaintEditPopup);
    this.$bus.$off('close-complaint-edit-popup', this.closeComplaintEditPopup);
  }
};
</script>

<style lang="scss">
.cursor-help {
  cursor: help;
}
</style>
